import React from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useChat } from '../../context/ChatContext';
import CodeBlock from './CodeBlock';
import type { ChatMessage as ChatMessageType } from '../../types/chat.types';
import './ChatMessage.css';

interface ChatMessageProps {
  message: ChatMessageType;
}

/**
 * Individual message component
 * Renders user or assistant messages with markdown support
 */
function ChatMessage({ message }: ChatMessageProps) {
  const { t } = useTranslation();
  const { sendMessage, currentBotId } = useChat();
  const isUser = message.role === 'user';

  const handleRetry = async () => {
    if (message.status === 'failed' && message.content) {
      await sendMessage(message.content, currentBotId);
    }
  };

  return (
    <div className={`message ${isUser ? 'user' : 'assistant'}`}>
      <div className="message-avatar">
        {isUser ? 'You' : 'AI'}
      </div>
      <div className="message-content">
        <div className="message-text">
          {isUser ? (
            <p>{message.content}</p>
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                code(props) {
                  const { node, inline, className, children, ...rest } = props as any;
                  const match = /language-(\w+)/.exec(className || '');
                  return !inline ? (
                    <CodeBlock
                      language={match ? match[1] : ''}
                      code={String(children).replace(/\n$/, '')}
                    />
                  ) : (
                    <code className="inline-code" {...rest}>
                      {children}
                    </code>
                  );
                },
                // Wrap tables in a scroll container so wide chapter tables
                // don't overflow the message bubble on narrow viewports.
                table({ children }) {
                  return (
                    <div className="message-table-wrapper">
                      <table>{children}</table>
                    </div>
                  );
                },
              }}
            >
              {message.content}
            </ReactMarkdown>
          )}
        </div>

        {/* Status indicators */}
        {message.status && (
          <div className="message-status">
            {message.status === 'sending' && (
              <span className="status-sending">{t('chat.sending')}</span>
            )}
            {message.status === 'failed' && (
              <div className="status-failed">
                <span>{t('chat.failed_message')}</span>
                <button
                  className="btn-retry"
                  onClick={handleRetry}
                  type="button"
                >
                  {t('chat.retry')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatMessage;
